// Lote Admin-V1 — Web client for the SILLEDA_ADMIN admin panel.
//
// Three surface areas:
//   • GET /admin/stats           → dashboard KPIs
//   • GET/PUT /admin/settings    → runtime feature flags
//   • GET/POST /admin/announcements → broadcast push history + compose
//
// Everything is gated by the backend `isAdmin()` helper; the SPA gate
// (`AdminPage`) is informational and not security-critical — the server
// returns 403 if the JWT doesn't carry SILLEDA_ADMIN.

import { apiClient } from './client';

export interface AdminStats {
  users: number;
  users_blocked: number;
  farms: number;
  animals: number;
  events: number;
  auctions_listed: number;
  merkle_anchors: number;
  // Lote Admin-V2 — count of distinct active users per role assignment.
  users_by_role: {
    FARMER: number;
    VET: number;
    BUYER: number;
    TRANSPORTISTA: number;
    INDUSTRY: number;
    SILLEDA_ADMIN: number;
  };
}

export async function getAdminStats(): Promise<AdminStats> {
  const { data } = await apiClient.get<AdminStats>('/api/v1/admin/stats');
  return data;
}

// ─── Users management (block / unblock) ──────────────────────────────────

export type AdminUserRole =
  | 'FARMER'
  | 'VET'
  | 'BUYER'
  | 'TRANSPORTISTA'
  | 'INDUSTRY'
  | 'SILLEDA_ADMIN';

export interface AdminUserRow {
  id: string;
  email: string | null;
  name: string;
  roles: AdminUserRole[];
  primary_role: AdminUserRole;
  blocked_at: string | null;
  block_reason: string | null;
  created_at: string;
}

export async function listAdminUsers(limit = 50): Promise<AdminUserRow[]> {
  const { data } = await apiClient.get<{ rows: AdminUserRow[] }>(
    `/api/v1/admin/users?limit=${limit}`,
  );
  return data.rows;
}

export async function blockUser(userId: string, reason: string): Promise<void> {
  await apiClient.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/block`, { reason });
}

export async function unblockUser(userId: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/users/${encodeURIComponent(userId)}/block`);
}

// ─── Platform settings (feature flags) ────────────────────────────────────

export type PlatformSettingKey = 'registration_open' | 'private_beta_mode';

export interface PlatformSettingItem {
  key: PlatformSettingKey;
  value: unknown;
  updated_at: string | null;
}

export async function listPlatformSettings(): Promise<PlatformSettingItem[]> {
  const { data } = await apiClient.get<{ settings: PlatformSettingItem[] }>(
    '/api/v1/admin/settings',
  );
  return data.settings;
}

export async function updatePlatformSetting(
  key: PlatformSettingKey,
  value: unknown,
): Promise<PlatformSettingItem> {
  const { data } = await apiClient.put<PlatformSettingItem>(
    `/api/v1/admin/settings/${encodeURIComponent(key)}`,
    { value },
  );
  return data;
}

// ─── Announcements ────────────────────────────────────────────────────────

export type AnnouncementTargetRole =
  | 'FARMER'
  | 'VET'
  | 'BUYER'
  | 'TRANSPORTISTA'
  | 'INDUSTRY'
  | 'SILLEDA_ADMIN';

export interface AnnouncementRow {
  id: string;
  title: string;
  body: string;
  target_role: AnnouncementTargetRole | null;
  sent_by: string;
  recipients_count: number;
  sent_at: string;
}

export async function listAnnouncements(limit = 20): Promise<AnnouncementRow[]> {
  const { data } = await apiClient.get<{ rows: AnnouncementRow[] }>(
    `/api/v1/admin/announcements?limit=${limit}`,
  );
  return data.rows;
}

export async function createAnnouncement(input: {
  title: string;
  body: string;
  target_role: AnnouncementTargetRole | null;
}): Promise<AnnouncementRow> {
  const { data } = await apiClient.post<AnnouncementRow>('/api/v1/admin/announcements', input);
  return data;
}

// Lote V-15 — Short-lived URL para abrir o pitch deck privado.
// Esixe SILLEDA_ADMIN + elevation 2FA (xa enforced pola ruta backend).
export async function getPitchTokenUrl(): Promise<{
  url: string;
  expires_in_seconds: number;
}> {
  const { data } = await apiClient.get<{ url: string; expires_in_seconds: number }>(
    '/api/v1/admin/pitch-token',
  );
  return data;
}
